/* eslint-disable react-native/no-inline-styles */
import React, {useCallback, useState} from 'react';
import {
  useAddItemContext,
  useSearchItemsForAddItems,
} from '../../../batchDetails/hooks/AddItem';

import {AddItemCell} from '../../../batchDetails/types';
import {Analytics} from '@buncha/config/analytics/analytics';
import {Box} from '../../../components/core/view';
import CustomActivityIndicator from '../../../components/composites/CustomActivityIndicator/CustomActivityIndicator';
import {CustomFlatList} from '../../../components/core/list';
import {Divider} from 'native-base';
import {EventType} from '@buncha/config/analytics/type';
import If from '@buncha/components/conditional/If';
import ItemAddCell from '../ItemAddCell';
import LoaderComponents from '@buncha/components/composites/CustomLoader/LoaderComponents';
import {Modules} from '@buncha/utils/types';
import SearchInput from '../../../components/composites/Input/TextInput/SearchInput';
import {SearchType} from '@buncha/itemDetails/types/itemReplacement';
import {ShopperScreens} from '@buncha/home/navigation/types';
import {StyleSheet} from 'react-native';
import {getClientType} from '@buncha/common/types/common';
import {styles} from '../../../batchDetails/styles/AddItemPage';
import {useColorThemeType} from '../../../appStyles/hooks/theme';
import {useGetLocale} from '@buncha/localization/hooks';
import {useIsMeijerStore} from '@buncha/home/hooks/home';
import {useRoute} from '@react-navigation/native';

interface SearchSectionProps {
  selectedItem: AddItemCell | undefined;
}

const SearchSection: React.FC<SearchSectionProps> = props => {
  const theme = useColorThemeType('main');

  const {state, dispatch} = useAddItemContext();

  const {searchResults} = state;

  const routeParams: any = useRoute().params;
  const storeId = Number(routeParams?.storeId);
  const isB2BStore = useIsMeijerStore(storeId);
  const runId = Number(routeParams?.runId);
  const size = 10;
  const searchSource = 'Add Item';
  const {body} =
    useGetLocale(Modules.batchDetails, ShopperScreens.AddItemPage) ?? {};

  const [searchQuery, setSearchQuery] = useState('');

  const {searchItemsForAddition, loading, moreItems} =
    useSearchItemsForAddItems(dispatch, state);

  const onFocus = useCallback(() => {
    Analytics.eventWithProps('Searching for an item', {
      type: EventType.input,
      source: searchSource,
      client: getClientType(isB2BStore),
    });
  }, [isB2BStore]);

  const searchForAnItemEvent = useCallback(
    (keyword: string, offset: number) => {
      Analytics.eventWithProps('Search for an item', {
        searchItem: keyword,
        from: offset,
        size: size,
        source: searchSource,
        storeId: storeId,
        runId: runId,
        client: getClientType(isB2BStore),
      });
    },
    [isB2BStore, runId, storeId],
  );

  const handleSearchQueryChange = useCallback(
    async (text: string) => {
      setSearchQuery(text);
      searchForAnItemEvent(text, 0);
      await searchItemsForAddition({
        storeId: storeId || 0,
        from: 0,
        size: size,
        searchQuery: text,
        runId: runId,
        searchType: SearchType.userSearch,
      });
    },
    [runId, searchForAnItemEvent, searchItemsForAddition, storeId],
  );

  const handleScroll = useCallback(
    async (_: any, offset: number) => {
      searchForAnItemEvent(searchQuery, offset);
      await searchItemsForAddition({
        storeId: storeId || 0,
        from: offset,
        size: size,
        searchQuery: searchQuery,
        runId: runId,
      });
    },
    [searchForAnItemEvent, searchQuery, searchItemsForAddition, storeId, runId],
  );

  const renderItem = useCallback((subItem: {item: AddItemCell}) => {
    return <ItemAddCell itemInfo={subItem?.item} />;
  }, []);

  const separator = useCallback(() => <Divider />, []);

  return (
    <Box style={styles.scrollView}>
      <SearchInput
        defaultValue={searchQuery}
        containerStyle={[
          styles.search,
          {
            backgroundColor: theme?.background?.white?.[100],
            borderColor: theme?.background.gray[100],
          },
        ]}
        debounce={true}
        placeholder={body?.searchItem}
        placeholderTextColor={theme?.text?.blue?.[300]}
        textValue={searchQuery}
        clearInput={true}
        colorThemeType="default"
        onChangeText={handleSearchQueryChange}
        style={{color: theme?.text?.blue?.[300]}}
        onFocus={onFocus}
      />
      <If condition={loading && searchResults?.length === 0}>
        <LoaderComponents showBottomText={false} />
      </If>
      {searchResults?.length > 0 && (
        <CustomFlatList
          data={searchResults}
          renderItem={renderItem}
          style={{marginBottom: props?.selectedItem ? 75 : 25}}
          ItemSeparatorComponent={separator}
          loadMore={moreItems}
          loading={loading}
          userOffset={10}
          showsVerticalScrollIndicator={false}
          initialOffset={10}
          onEndReachedThreshold={0.5}
          numOfTimesToRender={1}
          loadingSkeleton={
            <Box style={[PageStyles.activityIndicatorContainer]}>
              <CustomActivityIndicator />
            </Box>
          }
          onEndReachedAtt={handleScroll}
        />
      )}
    </Box>
  );
};

export default React.memo(SearchSection);

export const PageStyles = StyleSheet.create({
  activityIndicatorContainer: {
    flexDirection: 'row',
    flexGrow: 1,
    justifyContent: 'center',
    marginTop: 20,
  },
});
